import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Spinner } from '../components/Spinner';
import {
  TrendingUp,
  ShoppingBag,
  DollarSign,
  Users,
  Gift,
  Percent,
  Calendar,
  Package,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import styles from './ReportsPage.module.css';

type Preset =
  | 'thisMonth'
  | 'lastMonth'
  | 'lastQuarter'
  | 'last6Months'
  | 'lastYear'
  | 'allTime'
  | 'custom';

const PRESETS: { id: Preset; label: string }[] = [
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'lastQuarter', label: 'Last Quarter' },
  { id: 'last6Months', label: 'Last 6 Months' },
  { id: 'lastYear', label: 'Last Year' },
  { id: 'allTime', label: 'All Time' },
  { id: 'custom', label: 'Custom Range' },
];

const COLORS = ['#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ec4899', '#3b82f6', '#ef4444', '#84cc16'];

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cod: 'Cash on Delivery',
  instapay: 'Instapay',
  wallet: 'Mobile Wallet',
};

const PAINTING_TYPE_LABELS: Record<string, string> = {
  portrait: 'Portrait',
  landscape: 'Landscape',
  others: 'Others',
};

function computeRange(
  preset: Preset,
  customStart: string,
  customEnd: string,
): { startDate?: string; endDate?: string } {
  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  switch (preset) {
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startDate: start.toISOString(), endDate: endOfToday.toISOString() };
    }
    case 'lastMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      return { startDate: start.toISOString(), endDate: end.toISOString() };
    }
    case 'lastQuarter': {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return { startDate: start.toISOString(), endDate: endOfToday.toISOString() };
    }
    case 'last6Months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      return { startDate: start.toISOString(), endDate: endOfToday.toISOString() };
    }
    case 'lastYear': {
      const start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      return { startDate: start.toISOString(), endDate: endOfToday.toISOString() };
    }
    case 'custom': {
      return {
        startDate: customStart ? new Date(customStart).toISOString() : undefined,
        endDate: customEnd ? new Date(`${customEnd}T23:59:59`).toISOString() : undefined,
      };
    }
    case 'allTime':
    default:
      return {};
  }
}

interface Summary {
  totalRevenue: number;
  totalProductOrders: number;
  totalOrdersAllStatuses: number;
  totalItemsSold: number;
  averageOrderValue: number;
  uniqueCustomers: number;
  giftOrders: number;
  giftOrderPercent: number;
  discountedOrders: number;
  totalDiscountGiven: number;
  reservationCount: number;
  reservationSeatsBooked: number;
  reservationRevenue: number;
}

interface TrendPoint {
  date: string;
  revenue: number;
  orders: number;
}

interface TypeRow {
  type: string;
  revenue: number;
  orders: number;
}

interface NamedRow {
  id?: string;
  name: string;
  quantitySold: number;
  revenue: number;
  orders: number;
}

interface PaymentRow {
  method: string;
  orders: number;
  revenue: number;
}

interface GovernorateRow {
  governorate: string;
  orders: number;
  revenue: number;
}

interface WorkshopRow {
  id: string;
  name: string;
  capacity: number;
  seatsBooked: number;
  revenue: number;
  reservations: number;
}

interface ReportData {
  summary: Summary;
  revenueTrend: TrendPoint[];
  revenueByProductType: TypeRow[];
  topKits: NamedRow[];
  topPalettes: NamedRow[];
  categoryPerformance: NamedRow[];
  collectionPerformance: NamedRow[];
  paymentMethodBreakdown: PaymentRow[];
  paidStatusBreakdown: { paid: number; paidRevenue: number; unpaid: number; unpaidRevenue: number };
  statusBreakdown: { pending: number; confirmed: number; cancelled: number };
  reservationStatusBreakdown: { pending: number; confirmed: number; cancelled: number };
  governorateBreakdown: GovernorateRow[];
  customOrderTypeBreakdown: TypeRow[];
  workshopPerformance: WorkshopRow[];
}

function KpiCard({
  icon: Icon,
  label,
  value,
  subtext,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  subtext?: string;
  color: string;
}) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiIcon} style={{ backgroundColor: `${color}20`, color }}>
        <Icon size={22} />
      </div>
      <div>
        <span className={styles.kpiValue}>{value}</span>
        <span className={styles.kpiLabel}>{label}</span>
        {subtext && <span className={styles.kpiSubtext}>{subtext}</span>}
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {children}
    </div>
  );
}

export function ReportsPage() {
  const { token } = useAuth();
  const [preset, setPreset] = useState<Preset>('thisMonth');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => computeRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  useEffect(() => {
    if (preset === 'custom' && (!customStart || !customEnd)) return;

    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (range.startDate) params.set('startDate', range.startDate);
        if (range.endDate) params.set('endDate', range.endDate);

        const response = await fetch(`${API_BASE_URL}/api/reports?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to load reports');
        const json = await response.json();
        setData(json);
      } catch (err: any) {
        setError(err.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, range.startDate, range.endDate, preset]);

  const formatEGP = (n: number) => `EGP ${n.toLocaleString()}`;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Reports & Insights</h1>
          <p>Product performance and business statistics</p>
        </div>
      </header>

      <div className={styles.filterBar}>
        <div className={styles.presetGroup}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className={`${styles.presetBtn} ${preset === p.id ? styles.presetActive : ''}`}
              onClick={() => setPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className={styles.customRange}>
            <Calendar size={16} />
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span>to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
          </div>
        )}
      </div>

      {loading ? (
        <Spinner label="Loading reports..." size="lg" />
      ) : error ? (
        <div className={styles.empty}>{error}</div>
      ) : !data ? (
        <div className={styles.empty}>Select a custom date range to see reports.</div>
      ) : (
        <>
          <div className={styles.kpiGrid}>
            <KpiCard
              icon={DollarSign}
              label="Total Revenue"
              value={formatEGP(data.summary.totalRevenue)}
              subtext="excl. cancelled orders"
              color="#10b981"
            />
            <KpiCard
              icon={ShoppingBag}
              label="Product Orders"
              value={String(data.summary.totalProductOrders)}
              subtext={`${data.summary.totalOrdersAllStatuses} incl. cancelled`}
              color="#8b5cf6"
            />
            <KpiCard
              icon={TrendingUp}
              label="Avg. Order Value"
              value={formatEGP(data.summary.averageOrderValue)}
              color="#06b6d4"
            />
            <KpiCard
              icon={Package}
              label="Items Sold"
              value={String(data.summary.totalItemsSold)}
              color="#f59e0b"
            />
            <KpiCard
              icon={Users}
              label="Unique Customers"
              value={String(data.summary.uniqueCustomers)}
              color="#3b82f6"
            />
            <KpiCard
              icon={Gift}
              label="Gift Orders"
              value={String(data.summary.giftOrders)}
              subtext={`${data.summary.giftOrderPercent}% of orders`}
              color="#ec4899"
            />
            <KpiCard
              icon={Percent}
              label="Discount Given"
              value={formatEGP(data.summary.totalDiscountGiven)}
              subtext={`${data.summary.discountedOrders} discounted orders`}
              color="#ef4444"
            />
            <KpiCard
              icon={Calendar}
              label="Workshop Reservations"
              value={String(data.summary.reservationCount)}
              subtext={`${data.summary.reservationSeatsBooked} seats • ${formatEGP(data.summary.reservationRevenue)}`}
              color="#84cc16"
            />
          </div>

          <SectionCard title="Revenue Trend">
            {data.revenueTrend.length === 0 ? (
              <p className={styles.noData}>No orders in this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.revenueTrend}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: any) => formatEGP(Number(value))} />
                  <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" fill="url(#revenueGradient)" strokeWidth={2} name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </SectionCard>

          <div className={styles.twoCol}>
            <SectionCard title="Revenue by Product Type">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.revenueByProductType} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="type" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip formatter={(value: any) => formatEGP(Number(value))} />
                  <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                    {data.revenueByProductType.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </SectionCard>

            <SectionCard title="Payment Methods">
              {data.paymentMethodBreakdown.length === 0 ? (
                <p className={styles.noData}>No orders in this period.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={data.paymentMethodBreakdown}
                      dataKey="orders"
                      nameKey="method"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(entry: any) => PAYMENT_METHOD_LABELS[entry.method] || entry.method}
                    >
                      {data.paymentMethodBreakdown.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: any, _name: any, entry: any) => [
                        `${value} orders — ${formatEGP(entry.payload.revenue)}`,
                        PAYMENT_METHOD_LABELS[entry.payload.method] || entry.payload.method,
                      ]}
                    />
                    <Legend formatter={(value: string) => PAYMENT_METHOD_LABELS[value] || value} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </SectionCard>
          </div>

          <div className={styles.twoCol}>
            <SectionCard title="Order Status">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Pending', value: data.statusBreakdown.pending },
                      { name: 'Confirmed', value: data.statusBreakdown.confirmed },
                      { name: 'Cancelled', value: data.statusBreakdown.cancelled },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry: any) => `${entry.name}: ${entry.value}`}
                  >
                    <Cell fill="#f59e0b" />
                    <Cell fill="#10b981" />
                    <Cell fill="#ef4444" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </SectionCard>

            <SectionCard title="Payment Status">
              <div className={styles.paidStats}>
                <div className={styles.paidStatBlock}>
                  <span className={styles.paidStatValue} style={{ color: '#10b981' }}>
                    {data.paidStatusBreakdown.paid}
                  </span>
                  <span className={styles.paidStatLabel}>Paid Orders</span>
                  <span className={styles.paidStatSub}>{formatEGP(data.paidStatusBreakdown.paidRevenue)}</span>
                </div>
                <div className={styles.paidStatBlock}>
                  <span className={styles.paidStatValue} style={{ color: '#ef4444' }}>
                    {data.paidStatusBreakdown.unpaid}
                  </span>
                  <span className={styles.paidStatLabel}>Unpaid Orders</span>
                  <span className={styles.paidStatSub}>{formatEGP(data.paidStatusBreakdown.unpaidRevenue)}</span>
                </div>
              </div>
              {data.customOrderTypeBreakdown.length > 0 && (
                <>
                  <h3 className={styles.subheading}>Custom Order Types</h3>
                  <div className={styles.tagRow}>
                    {data.customOrderTypeBreakdown.map((t) => (
                      <span key={t.type} className={styles.tagChip}>
                        {PAINTING_TYPE_LABELS[t.type] || t.type}: {t.orders} ({formatEGP(t.revenue)})
                      </span>
                    ))}
                  </div>
                </>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Top Painting Kits">
            {data.topKits.length === 0 ? (
              <p className={styles.noData}>No kit orders in this period.</p>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Kit</th>
                    <th>Orders</th>
                    <th>Qty Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topKits.map((k) => (
                    <tr key={k.id}>
                      <td>{k.name}</td>
                      <td>{k.orders}</td>
                      <td>{k.quantitySold}</td>
                      <td>{formatEGP(k.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <div className={styles.twoCol}>
            <SectionCard title="Top Color Palettes">
              {data.topPalettes.length === 0 ? (
                <p className={styles.noData}>No palette orders in this period.</p>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Palette</th>
                      <th>Orders</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topPalettes.map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>{p.orders}</td>
                        <td>{formatEGP(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>

            <SectionCard title="Collection Performance">
              {data.collectionPerformance.length === 0 ? (
                <p className={styles.noData}>No collection sales in this period.</p>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Collection</th>
                      <th>Orders</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.collectionPerformance.map((c) => (
                      <tr key={c.name}>
                        <td>{c.name}</td>
                        <td>{c.orders}</td>
                        <td>{formatEGP(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Category Performance">
            {data.categoryPerformance.length === 0 ? (
              <p className={styles.noData}>No category sales in this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(200, data.categoryPerformance.length * 40)}>
                <BarChart data={data.categoryPerformance} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={130} />
                  <Tooltip formatter={(value: any) => formatEGP(Number(value))} />
                  <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                    {data.categoryPerformance.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>

          <SectionCard title="Orders by Governorate">
            {data.governorateBreakdown.length === 0 ? (
              <p className={styles.noData}>No orders in this period.</p>
            ) : (
              <ResponsiveContainer width="100%" height={Math.max(200, data.governorateBreakdown.length * 36)}>
                <BarChart data={data.governorateBreakdown} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="governorate" tick={{ fontSize: 12 }} width={110} />
                  <Tooltip formatter={(value: any) => formatEGP(Number(value))} />
                  <Bar dataKey="revenue" fill="#06b6d4" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </SectionCard>

          {data.workshopPerformance.length > 0 && (
            <SectionCard title="Workshop Performance">
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Workshop</th>
                    <th>Reservations</th>
                    <th>Seats Booked</th>
                    <th>Capacity</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {data.workshopPerformance.map((w) => (
                    <tr key={w.id}>
                      <td>{w.name}</td>
                      <td>{w.reservations}</td>
                      <td>{w.seatsBooked}</td>
                      <td>{w.capacity}</td>
                      <td>{formatEGP(w.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}